import React, { createContext, useContext, useState } from 'react';
import { featureFlagsContext } from '../../../providers/FeatureFlagsProvider';

export enum ShowByType {
  DEFAULT = 'default',
  COUNTY = 'county',
  ZIP = 'zip',
}

export enum MapType {
  STANDARD = 'standard',
  MONOCHROME = 'monochrome',
  HYBRID = 'hybrid',
  SATELLITE = 'satellite',
}

export interface OutageToggles {
  unplanned: boolean;
  planned: boolean;
  psps?: boolean;
}

export interface CountyOutage {
  county: string;
  count: number;
  center: [number, number];
}

export interface ZipOutage {
  zipCode: string;
  count: number;
  center: [number, number];
}

interface OutageMapOptionsContextProps {
  showBy: ShowByType;
  setShowBy: (val: ShowByType) => void;

  outageToggles: OutageToggles;
  setOutageToggles: React.Dispatch<React.SetStateAction<OutageToggles>>;

  mapStyle: MapType;
  setMapStyle: (val: MapType) => void;
  toggledOnCount: number;

  countyOutagesCenters: CountyOutage[];
  setCountyOutagesCenters: (val: CountyOutage[]) => void;

  zipOutagesCenters: ZipOutage[];
  setZipOutagesCenters: (val: ZipOutage[]) => void;
}

const OutageMapOptionsContext = createContext<
  OutageMapOptionsContextProps | undefined
>(undefined);

export const OutageMapOptionsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [showBy, setShowBy] = useState<ShowByType>(ShowByType.DEFAULT);
  const [mapStyle, setMapStyle] = useState<MapType>(MapType.STANDARD);
  const { isOutagesPSPSEnabled, loading } = useContext(featureFlagsContext);

  const [outageToggles, setOutageToggles] = useState<OutageToggles>({
    unplanned: true,
    planned: true,
    ...(!loading && isOutagesPSPSEnabled ? { psps: false } : {}),
  });

  const [countyOutagesCenters, setCountyOutagesCenters] = useState<
    CountyOutage[]
  >([]);
  const [zipOutagesCenters, setZipOutagesCenters] = useState<ZipOutage[]>([]);

  const value = React.useMemo(() => {
    const toggledOnCount = Object.values(outageToggles).filter(Boolean).length;

    return {
      showBy,
      setShowBy,
      outageToggles,
      setOutageToggles,
      mapStyle,
      setMapStyle,
      toggledOnCount,
      countyOutagesCenters,
      setCountyOutagesCenters,
      zipOutagesCenters,
      setZipOutagesCenters,
    };
  }, [
    showBy,
    outageToggles,
    mapStyle,
    countyOutagesCenters,
    zipOutagesCenters,
  ]);

  return (
    <OutageMapOptionsContext.Provider value={value}>
      {children}
    </OutageMapOptionsContext.Provider>
  );
};

export const useOutageMapOptions = (): OutageMapOptionsContextProps => {
  const ctx = useContext(OutageMapOptionsContext);
  if (!ctx) {
    throw new Error(
      'useOutageMapOptions must be used within OutageMapOptionsProvider',
    );
  }
  return ctx;
};
